/**
 * The inbound replies table (prototype `vSuppliers`, the `inbound` block).
 *
 * ---- One row per RFQ, and TWO things you can open ----
 *
 * A real enquiry is a dozen suppliers quoting fifty lines each. Rendering the mail, the priced
 * lines and the parse button inline turns the one table a purchaser scans into a page they scroll
 * past, which is the state this screen was in. So the row carries only what decides whether to
 * look closer — how many lines, what they come to, whether a UOM is wrong — and the detail sits
 * behind two distinct clicks:
 *
 *   click the RFQ      → its priced lines expand beneath it, ONE RFQ at a time
 *   click the SUPPLIER → their reply mail opens in the docked panel beside the table
 *
 * One at a time for the lines is the point: fifty rows is readable, six hundred is not, and an
 * accordion makes that impossible to reach by accident. The mail never expands the table at all.
 *
 * ---- Why the per-item columns are gone from the row ----
 *
 * UOM / Rate / Offer remarks / Currency describe a LINE, not an RFQ. At row level they showed "—"
 * on everything unquoted — four empty columns on most of the table, which is most of what made it
 * look broken. They now exist only in the expanded sub-rows, under their own headers.
 */

import { useState } from 'react'
import { Badge } from '../../components/Badge'
import { Button, Card } from '../../components/ui'
import { Money } from '../../components/Money'
import { formatAgo, formatDate, formatTime } from '../../lib/format'
import { ratePerRfqUom, replyRowKey } from '../../lib/sq'
import type { ReplyRow } from '../../lib/sq'
import { premiumOverBest, type Benchmark } from '../../lib/quoteBenchmark'
import { previousQuoteBy } from '../../lib/api/useItemQuoteHistory'
import type { Currency, Item, Money as MoneyValue, PurchaseHistoryEntry, SupplierQuotationItem } from '../../lib/types'
import { ReplyOffer, useOfferSummary } from './ReplyOffer'

/** RFQ-UOM vs SQ-UOM, with the server's mismatch verdict rendered as the badge. */
function UomCell({ item, master }: { item: SupplierQuotationItem; master: Item | undefined }) {
  const perRfqUom = ratePerRfqUom(item, master)

  return (
    <>
      RFQ <b>{item.rfq_uom}</b> · SQ <b>{item.sq_uom}</b>{' '}
      {item.uom_mismatch ? <Badge tone="danger">⚠ UOM mismatch</Badge> : <Badge tone="ok">✓</Badge>}
      {item.uom_mismatch ? (
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
          {perRfqUom === null ? (
            'no master conversion — convert manually'
          ) : (
            <>
              ≈ <Money value={{ amount: perRfqUom, currency: item.rate.currency }} />/{item.rfq_uom}
            </>
          )}
        </div>
      ) : null}
    </>
  )
}

/**
 * The summary for a reply nobody has read yet — read from the workbook they attached.
 *
 * Its own component because it needs a hook, and a hook cannot be called inside the row `.map()`.
 * Renders nothing until the figures are known: a row must never show a total it is still guessing.
 */
function OfferSummaryCell({ replyId, rfqId }: { replyId: string; rfqId: string }) {
  const summary = useOfferSummary(replyId, rfqId)
  if (summary === null) return <span style={{ color: 'var(--muted)' }}>reading their workbook…</span>
  if (summary.priced === 0) return <span style={{ color: 'var(--muted)' }}>returned with no prices filled in</span>

  return (
    <>
      <b>
        {summary.priced} line{summary.priced === 1 ? '' : 's'}
      </b>{' '}
      priced
      {summary.currency !== '' ? (
        <>
          {' · '}
          <b>
            <Money value={{ amount: summary.total, currency: summary.currency as Currency }} />
          </b>
        </>
      ) : null}
      <div style={{ fontSize: 11, color: 'var(--muted)' }}>not yet a quotation</div>
    </>
  )
}

export function InboundRepliesTable({
  rows,
  itemsByCode,
  benchmarks,
  quoteHistories,
  quoteHistoryFailed,
  onRemind,
  remindingRfqId,
  onOpenSupplier,
  openSupplierRowKey,
  lineText,
}: {
  rows: ReplyRow[]
  itemsByCode: Map<string, Item>
  /**
   * The enquiry line's printed description by row number — the purchaser's edit, else the
   * customer's wording. A quoted row is shown as the line it prices, not as the master names it
   * (Akhil, 2026-10-16: "general paint" written over a master name is what was quoted).
   */
  lineText: Map<string, string>
  /**
   * Cheapest quote per item across every reply on this enquiry — so a rate can be read as dear or
   * cheap without opening the comparison workbench. See `lib/quoteBenchmark.ts`.
   */
  benchmarks: Map<string, Benchmark>
  /**
   * Every past quote per item code, so a line can show what THIS supplier charged last time.
   * Empty until the batch read resolves — the rows simply show nothing until then.
   */
  quoteHistories: Map<string, PurchaseHistoryEntry[]>
  /**
   * The past-price read failed, as opposed to finding nothing. Without the distinction both show
   * as a blank rate column and "where is the history?" has no answer.
   */
  quoteHistoryFailed: boolean
  onRemind: (rfqId: string) => void
  remindingRfqId: string | null
  /** Open this RFQ's correspondence in the docked panel. */
  onOpenSupplier: (rowKey: string) => void
  /** Which supplier's panel is open, so the row can show itself as selected. */
  openSupplierRowKey: string | null
}) {
  // ONE open at a time. See the module header: fifty expanded lines is readable, six hundred is
  // not, and an accordion makes the second state unreachable rather than merely discouraged.
  const [openRow, setOpenRow] = useState<string | null>(null)
  const toggle = (key: string) => setOpenRow((current) => (current === key ? null : key))

  const answered = rows.filter((r) => r.state !== 'awaiting').length

  return (
    <Card padded={false}>
      <div className="pad" style={{ borderBottom: '1px solid var(--line)', color: 'var(--muted)', fontSize: 12 }}>
        {rows.length} RFQ{rows.length === 1 ? '' : 's'} · {answered} answered — click an <b>RFQ</b> to see its lines,
        or a <b>supplier</b> to read their mail.
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>RFQ</th>
              <th>Supplier</th>
              <th>What came back</th>
              <th>Status</th>
            </tr>
          </thead>

          {rows.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={4} style={{ color: 'var(--muted)', textAlign: 'center', padding: 22 }}>
                  No RFQs have been sent for this requirement yet.
                </td>
              </tr>
            </tbody>
          ) : null}

          {rows.map((row) => {
            const { rfq, quotation, replies, state } = row
            // NOT `rfq.id`: five quotations can share one RFQ, and keying on it opens all five.
            const rowKey = replyRowKey(row)
            const open = openRow === rowKey
            const items = quotation?.items ?? []
            const flagged = items.filter((item) => item.uom_mismatch).length
            const newestReply = replies[0] ?? null
            // Only a row with something to show is clickable — an unanswered RFQ has no lines.
            const hasDetail = state === 'quoted' ? items.length > 0 : state === 'replied' && newestReply !== null
            const supplierLabel = rfq.supplier_name ?? rfq.supplier

            return (
              <tbody key={rowKey}>
                <tr>
                  <td className="mono">
                    {hasDetail ? (
                      <button
                        type="button"
                        className="linkish"
                        style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer' }}
                        aria-expanded={open}
                        onClick={() => toggle(rowKey)}
                      >
                        <span style={{ color: 'var(--muted)' }}>{open ? '▾ ' : '▸ '}</span>
                        {rfq.id}
                      </button>
                    ) : (
                      rfq.id
                    )}
                    {quotation ? (
                      <div style={{ color: 'var(--muted)', fontSize: 11 }}>{quotation.id}</div>
                    ) : null}
                  </td>

                  <td>
                    {/* Opens the mail, never the lines — the two are different questions and the
                        purchaser should not have to guess which click gives which. */}
                    <button
                      type="button"
                      className="linkish"
                      style={{
                        background: 'none',
                        border: 0,
                        padding: 0,
                        font: 'inherit',
                        fontWeight: 700,
                        cursor: 'pointer',
                        textAlign: 'left',
                        color: openSupplierRowKey === rowKey ? 'var(--brand)' : undefined,
                      }}
                      onClick={() => onOpenSupplier(rowKey)}
                    >
                      {supplierLabel}
                    </button>
                    <div style={{ color: 'var(--muted)', fontSize: 11 }}>
                      {replies.length > 0
                        ? `✉ ${replies.length} ${replies.length === 1 ? 'reply' : 'replies'} · read mail`
                        : 'no mail yet'}
                    </div>
                  </td>

                  <td style={{ color: 'var(--muted)' }}>
                    {state === 'quoted' && items.length > 0 ? (
                      <>
                        <b style={{ color: 'var(--ink)' }}>
                          {items.length} line{items.length === 1 ? '' : 's'}
                        </b>{' '}
                        priced
                        {quotation?.grand_total ? (
                          <>
                            {' · '}
                            <b style={{ color: 'var(--ink)' }}>
                              <Money value={quotation.grand_total} />
                            </b>
                          </>
                        ) : null}
                        <div style={{ fontSize: 11, marginTop: 2 }}>
                          {flagged > 0 ? (
                            <Badge tone="danger">
                              ⚠ {flagged} UOM mismatch{flagged === 1 ? '' : 'es'}
                            </Badge>
                          ) : (
                            <Badge tone="ok">UOMs match</Badge>
                          )}
                          <span style={{ marginLeft: 6 }}>{open ? 'hide lines' : 'show lines'}</span>
                        </div>
                      </>
                    ) : state === 'quoted' ? (
                      'Reply received with no priced lines.'
                    ) : state === 'replied' && newestReply ? (
                      <OfferSummaryCell replyId={newestReply.id} rfqId={rfq.id} />
                    ) : (
                      <>
                        {rfq.item_count} line{rfq.item_count === 1 ? '' : 's'} sent — nothing back
                      </>
                    )}
                  </td>

                  <td>
                    {state === 'quoted' ? (
                      <>
                        <Badge tone="ok">
                          Quoted{quotation?.received_at ? ` ${formatTime(quotation.received_at)}` : ''}
                        </Badge>
                        <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 3 }}>
                          {quotation?.received_at ? formatAgo(quotation.received_at) : 'reply parsed'} · in comparison
                        </div>
                      </>
                    ) : state === 'replied' ? (
                      <>
                        <Badge tone="info">Replied · not read</Badge>
                        <div style={{ color: 'var(--muted)', fontSize: 11, margin: '3px 0 5px' }}>
                          {rfq.sent_at ? `sent ${formatAgo(rfq.sent_at)}` : 'not sent yet'}
                          {/* The last chase, with its date and time (Akhil, 2026-09-16) — from
                              `reminder_sent_at`, which `sent_at` no longer absorbs. */}
                          {rfq.reminded_at ? (
                            <div title={`Reminder sent ${formatDate(rfq.reminded_at)} ${formatTime(rfq.reminded_at)}`}>
                              reminded {formatAgo(rfq.reminded_at)} · {formatDate(rfq.reminded_at)} {formatTime(rfq.reminded_at)}
                            </div>
                          ) : null}
                        </div>
                        {/* Reading it is done in the panel, beside the mail it is being read FROM. */}
                        <Button size="sm" onClick={() => onOpenSupplier(rowKey)}>
                          Read their reply
                        </Button>
                      </>
                    ) : (
                      <>
                        <Badge tone="warn">Awaiting</Badge>
                        <div style={{ color: 'var(--muted)', fontSize: 11, margin: '3px 0 5px' }}>
                          {/* Never the bare word "sent" for an RFQ with no `sent_at` — that read as
                              a claim it had gone out when it had not. */}
                          {rfq.sent_at ? `sent ${formatAgo(rfq.sent_at)}` : 'not sent yet'}
                        </div>
                        {/* Remind ONLY an RFQ that actually went out. A chase on a Draft mails the
                            supplier "we sent you RFQ-… and have not yet received your quotation",
                            which is false, AND stamps `reminder_sent_at` rather than `sent_at`, so
                            `buildRfqPlan` still reads it unsent and mails the same workbook later
                            as a FIRST send. `sent_at` is the only honest evidence it ever left. */}
                        {rfq.sent_at ? (
                          <Button size="sm" disabled={remindingRfqId === rfq.id} onClick={() => onRemind(rfq.id)}>
                            {remindingRfqId === rfq.id ? 'Sending…' : 'Remind'}
                          </Button>
                        ) : (
                          <span style={{ fontSize: 11, color: 'var(--warn)' }}>
                            never sent — raise it from item triage
                          </span>
                        )}
                      </>
                    )}
                  </td>
                </tr>

                {/* Quoted lines: one sub-row each, under a header of their own. */}
                {open && state === 'quoted'
                  ? [
                      <tr key={`${rowKey}-head`} className="subrow">
                        <td />
                        <td style={{ color: 'var(--muted)', fontSize: 11 }}>ITEM</td>
                        <td style={{ color: 'var(--muted)', fontSize: 11 }}>UOM (RFQ vs SQ)</td>
                        <td style={{ color: 'var(--muted)', fontSize: 11 }}>RATE · REMARKS</td>
                      </tr>,
                      /*
                        Says which of the two blank states this is. A line saying nothing about
                        past prices could mean "never quoted before" or "we could not look" — and
                        those call for completely different reactions from a purchaser.
                      */
                      <tr key={`${rowKey}-hist`} className="subrow">
                        <td />
                        <td colSpan={3} style={{ color: 'var(--muted)', fontSize: 11 }}>
                          {quoteHistoryFailed
                            ? '⚠ Past prices could not be read, so no comparison is shown below.'
                            : items.every(
                                  (i) =>
                                    !previousQuoteBy(
                                      quoteHistories.get(i.item_code),
                                      quotation?.supplier ?? rfq.supplier,
                                      quotation?.id ?? '',
                                    ),
                                )
                              ? 'No earlier quote from this supplier for these items — nothing to compare against yet.'
                              : 'Past prices below compare each line with this supplier’s own previous quote.'}
                        </td>
                      </tr>,
                      ...items.map((item) => (
                        <tr key={`${rowKey}-${item.item_code}`} className="subrow">
                          <td />
                          <td>
                            {(() => {
                              const master = itemsByCode.get(item.item_code)?.name
                              const text = lineText.get(item.enquiry_row_no) || master || item.item_code
                              return (
                                <>
                                  <div>{text}</div>
                                  <div style={{ color: 'var(--muted)', fontSize: 11 }} className="mono">
                                    {master && master !== text ? `${master} · ` : ''}
                                    {item.item_code} · stock {item.stock}
                                  </div>
                                </>
                              )
                            })()}
                          </td>
                          <td>
                            <UomCell item={item} master={itemsByCode.get(item.item_code)} />
                          </td>
                          <td>
                            <Money value={item.rate} />
                            {/*
                              How this rate compares with the cheapest anyone quoted for the same
                              item on this enquiry — the question a purchaser is actually asking
                              while reading a reply. Silent when this IS the cheapest, or when only
                              one supplier quoted it: "0% above" is noise and "18% above" against
                              nobody is a lie. See `premiumOverBest`.
                            */}
                            <PricePremium rate={item.rate} benchmark={benchmarks.get(item.item_code)} />
                            {/* And what this same supplier charged last time — the other half of
                                "is this price good?". Excludes the quotation on screen, or every
                                line would compare against itself. */}
                            <SupplierPriorPrice
                              rate={item.rate}
                              previous={previousQuoteBy(
                                quoteHistories.get(item.item_code),
                                quotation?.supplier ?? rfq.supplier,
                                quotation?.id ?? '',
                              )}
                            />
                            {item.remark ? (
                              <div style={{ color: 'var(--muted)', fontSize: 11 }}>{item.remark}</div>
                            ) : null}
                          </td>
                        </tr>
                      )),
                    ]
                  : null}

                {/* Unread reply: the lines as the supplier filled them into the workbook. */}
                {open && state === 'replied' && newestReply ? (
                  <tr className="subrow">
                    <td />
                    <td colSpan={3} style={{ paddingTop: 0 }}>
                      <ReplyOffer replyId={newestReply.id} rfqId={rfq.id} />
                    </td>
                  </tr>
                ) : null}
              </tbody>
            )
          })}
        </table>
      </div>
    </Card>
  )
}

/**
 * "▲ 18% above best (Top Go FZE)" — or nothing at all.
 *
 * Nothing is the common and correct case: the cheapest quote needs no badge, and a single quote
 * has nothing to be compared with.
 */
function PricePremium({ rate, benchmark }: { rate: MoneyValue; benchmark: Benchmark | undefined }) {
  const premium = premiumOverBest(rate, benchmark)
  if (premium === null) {
    // Say it explicitly when this IS the best of several — that is worth seeing.
    if (benchmark && benchmark.quotedBy > 1 && rate.amount === benchmark.best.amount) {
      return (
        <div style={{ fontSize: 11, color: 'var(--ok)' }} title={`Cheapest of ${benchmark.quotedBy} quotes`}>
          ✓ best of {benchmark.quotedBy}
        </div>
      )
    }
    return null
  }
  return (
    <div
      style={{ fontSize: 11, color: premium >= 20 ? 'var(--danger)' : 'var(--warn)' }}
      title={`Cheapest was ${benchmark!.best.amount} ${benchmark!.best.currency} from ${benchmark!.bestSupplier}`}
    >
      ▲ {premium}% above best ({benchmark!.bestSupplier})
    </div>
  )
}

/**
 * "was AED 10.00 · ▲ 25% vs their last quote" — this supplier against their own history.
 *
 * Distinct from `PricePremium`, which compares them with OTHER suppliers on this enquiry. Both
 * answer "is this price good?" and neither substitutes for the other: a supplier can be the
 * cheapest available and still have put their price up 30%.
 */
function SupplierPriorPrice({ rate, previous }: { rate: MoneyValue; previous: PurchaseHistoryEntry | undefined }) {
  if (!previous) return null
  // Comparing across currencies needs an FX rate this row has not got; the old price is still
  // worth showing on its own.
  const comparable = previous.price.currency === rate.currency && previous.price.amount > 0
  const change = comparable ? Math.round(((rate.amount - previous.price.amount) / previous.price.amount) * 100) : null
  return (
    <div style={{ fontSize: 11, color: 'var(--muted)' }} title={`Their quote on ${formatDate(previous.date)} (${previous.ref})`}>
      was <Money value={previous.price} />
      {change !== null && change !== 0 ? (
        <span style={{ color: change > 0 ? 'var(--danger)' : 'var(--ok)', marginLeft: 6 }}>
          {change > 0 ? '▲' : '▼'} {Math.abs(change)}%
        </span>
      ) : null}
    </div>
  )
}
